/**
 * blockChildBandFrame — a block-child polyline's WIDTH BAND under a
 * NON-SIMILARITY INSERT (@codex PR #785 round 6, P2).
 *
 * # The defect
 *
 * `transformBlockChild` clones a block child into world space, and for every
 * width-carrying field it multiplied the authored value by one scalar,
 * `|scale.x|`. That is EXACT for a similarity (`|sx| === |sy|`, mirror
 * included), because a similarity multiplies every length by the same factor.
 * It is not a width at all for anything else: the image of a constant-width
 * band under `S(2, 3)` is a band whose offset vector is `L·(h·n̂)` — neither
 * perpendicular to the mapped centreline nor scaled by any single number. A
 * horizontal span of width 2 under `S(2, 3)` is 6 wide in world Y; the clone
 * said 4, so the canvas drew a visibly thinner band.
 *
 * The vector plot never had it. `print/pdf/converters/insertConverter.ts`
 * hands the RAW block-local child and the composed block affine to
 * `lwpolylineBand(child, state.matrix)`, which builds the band from the
 * authored vertices and authored widths and maps the finished LOOPS through
 * the affine — "offset in the block's LOCAL frame and then mapped through the
 * block affine", as its own comment puts it. So screen and paper disagreed on
 * exactly the inserts nobody had a fixture for.
 *
 * # The same seam as round 5
 *
 * Round 5 (`../geom/polylinePlane.ts`) closed the OCS half of one sentence: a
 * width is not a scalar under a general map, so build the band in the frame
 * the width is authored in and map the finished band, rather than pushing a
 * scalar through. This is the INSERT half of that same sentence, and it is
 * closed with the same shape — a pair of exact inverse maps handed to the ONE
 * band entry point, `widePolylineBandLoops`. The only difference is that an
 * INSERT affine is not orthonormal, so the pair is a real matrix inverse
 * ({@link linearPolylinePlane}) rather than a transpose.
 *
 * # What this module decides, and the ONE place it is read
 *
 * {@link blockChildBandFrame} answers "is the clone's width still the authored
 * width?" — `null` means the scalar collapse was applied and the render path
 * must change nothing (the common case, byte-identical), non-`null` means the
 * clone carries the AUTHORED width and the frame says how the band built from
 * it maps to world. `transformBlockChild` reads it to choose the width factor
 * and `blockFromEntity` reads it to stamp the frame onto the finished
 * renderable, so the two can never drift into scaling the width AND mapping
 * the band.
 *
 * # Which kinds, and what the frame composes
 *
 * Only the kinds whose clone actually reaches the band: `LWPOLYLINE` and the
 * heavy `POLYLINE`. `Revision Cloud` stores an LWPOLYLINE-shaped vertex list
 * and a `constantWidth`, but `revisionCloudGeometry.ts` emits a `polyline`
 * geometry with NO `widths` field at all — the scallops are procedural — so its
 * width scalar is model fidelity only and is deliberately left alone. `MText`'s
 * `referenceWidth` is a text WRAP column, not a band, and keeps `|scale.x|` for
 * the same reason.
 *
 * The frame is the map from the frame the WIDTH is authored in to the frame the
 * scene geometry's POINTS are expressed in, which is exactly how far the
 * pipeline moved those points:
 *
 *   - LWPOLYLINE — `transformBlockChild` maps the raw group 10/20 x/y straight
 *     through the insert affine and `lwpolylineFromEntity` reads them as WCS
 *     XY. The entity's own group 210 is NOT consumed anywhere on that path
 *     (a documented pre-existing residual — the renderer has no LWPOLYLINE OCS
 *     lift), so composing it here would assume a lift that never happened.
 *     Frame = the insert's linear part alone.
 *   - heavy POLYLINE — a 2D subtype with a non-default extrusion IS lifted, by
 *     `ocsPlanarTransform`, before the affine, so the points are
 *     `A(L_ocs(p))`. Frame = `A · L_ocs`, the two composed. Every other
 *     subtype (3D polyline, mesh, default extrusion) never left its authored
 *     frame, so frame = `A` alone. Mesh subtypes carry no band at all.
 *
 * Translation is deliberately absent: band construction is
 * translation-equivariant, so the insertion point and the renderer's
 * coordinate-origin offset ride through the frame as a translation of its own
 * coordinates and cancel exactly (which is also why
 * `sceneGeometryWorldCorrection.ts` must not touch the field — it is a triple
 * of direction vectors).
 */

import {
  computeOcsAxes,
  isDefaultExtrusion,
  polylineSubtype,
} from "@dxf-ai-editor/drawing-model";
import type { BlockChildEntity } from "@dxf-ai-editor/drawing-model";

import type { PolylineBandFrame, RenderableEntity, Vec3 } from "../../types";
import { boundsFromPoints } from "../adapterContext";
import { linearPolylinePlane } from "../geom/polylinePlane";
import { widePolylineBandLoops } from "../geom/polylineWidthBand";

import {
  transformBlockDirection,
  type BlockInsertTransform,
} from "./blockInsertTransform";

/**
 * `true` when the insert's linear part is a SIMILARITY — `|sx| === |sy|`,
 * which covers the identity, every uniform scale, every mirror and every
 * rotation, with or without the inner `preRotationDeg` (a uniform scale
 * commutes with a rotation, so `R·S·R` collapses to `R·S`).
 *
 * This is the ONE predicate that decides whether a length is a scalar under
 * this transform, and it is deliberately the SAME test the Arc arm branches on
 * for its ellipse promotion (`transformBlockChild.ts`), so the two arms can
 * never disagree about which case they are in.
 */
export function isSimilarityTransform(t: BlockInsertTransform): boolean {
  return Math.abs(t.scale.x) === Math.abs(t.scale.y);
}

/** The insert's 3×3 linear part, as the images of the three unit axes. */
function insertLinearFrame(t: BlockInsertTransform): PolylineBandFrame {
  return {
    ax: transformBlockDirection({ x: 1, y: 0, z: 0 }, t),
    ay: transformBlockDirection({ x: 0, y: 1, z: 0 }, t),
    az: transformBlockDirection({ x: 0, y: 0, z: 1 }, t),
  };
}

/**
 * The child's OWN frame, when the flatten lifted it out of one before applying
 * the insert — today only the heavy 2D `POLYLINE` with a non-default group 210,
 * whose OCS `ocsPlanarTransform` consumes (see the module doc for why
 * LWPOLYLINE deliberately does not).
 */
function childOwnFrame(child: BlockChildEntity): PolylineBandFrame | null {
  if (child.type !== "Polyline") return null;
  if (polylineSubtype(child) !== "2d") return null;
  if (isDefaultExtrusion(child.extrusion)) return null;
  const { ax, ay, az } = computeOcsAxes(child.extrusion);
  return { ax, ay, az };
}

/**
 * The band frame for one block child, or `null` when the clone's width scalar
 * is already the exact image and nothing downstream must change.
 *
 * `null` for: every kind that carries no band, every SIMILARITY insert (the
 * byte-identical common path), and a singular linear part (a zero scale
 * component — `linearPolylinePlane` could not invert it, so the scalar
 * collapse's degenerate output is kept rather than emitting `NaN`).
 */
export function blockChildBandFrame(
  child: BlockChildEntity,
  t: BlockInsertTransform,
): PolylineBandFrame | null {
  if (child.type !== "LWPOLYLINE" && child.type !== "Polyline") return null;
  if (isSimilarityTransform(t)) return null;
  const insert = insertLinearFrame(t);
  const own = childOwnFrame(child);
  // `transformBlockDirection` IS the insert's linear map, so applying it to
  // each column of the child's own basis is the matrix product `A · L_ocs` —
  // no second composition rule to drift from the point map.
  const frame: PolylineBandFrame = own
    ? {
        ax: transformBlockDirection(own.ax, t),
        ay: transformBlockDirection(own.ay, t),
        az: transformBlockDirection(own.az, t),
      }
    : insert;
  return linearPolylinePlane(frame) ? frame : null;
}

/**
 * Stamp the band frame onto a finished block-child renderable, and widen its
 * AABB to the band that frame actually draws.
 *
 * The bounds half is not optional bookkeeping. `lwpolylineGeometry.ts` pads the
 * centreline box by the largest authored HALF WIDTH, measured in the points'
 * own space — and with the scalar collapse gone those widths are the
 * BLOCK-LOCAL ones, so the pad would stop short of the drawn band by the whole
 * insert scale. The real silhouette is unioned in instead of the pad being
 * widened by a guess, exactly as `polylineFromEntity.ts` does for its own
 * tilted band: a prefilter box may over-report, never under-report, or a
 * window selection at the band edge would drop a VISIBLE entity (CLAUDE.md
 * ПРИОРИТЕТ #1). Note this also repairs the SAME under-report on the old path,
 * where a `(2, 3)` insert padded by `|sx| = 2` a band that reached `3`.
 *
 * A child with no frame, no band or a non-polyline geometry is returned
 * unchanged — the same object, not a copy.
 */
export function applyBlockChildBandFrame(
  renderable: RenderableEntity,
  child: BlockChildEntity,
  t: BlockInsertTransform,
): RenderableEntity {
  const geometry = renderable.geometry;
  if (geometry.kind !== "polyline") return renderable;
  const widths = geometry.widths;
  if (!widths || widths.length === 0) return renderable;
  const bandFrame = blockChildBandFrame(child, t);
  if (!bandFrame) return renderable;

  const bounds = { ...renderable.bounds };
  for (const loop of widePolylineBandLoops(
    geometry.points,
    geometry.closed,
    geometry.bulges,
    widths,
    geometry.extrusion,
    bandFrame,
  )) {
    if (loop.length === 0) continue;
    const box = boundsFromPoints(loop as Vec3[]);
    bounds.minX = Math.min(bounds.minX, box.minX);
    bounds.minY = Math.min(bounds.minY, box.minY);
    bounds.maxX = Math.max(bounds.maxX, box.maxX);
    bounds.maxY = Math.max(bounds.maxY, box.maxY);
  }

  return { ...renderable, geometry: { ...geometry, bandFrame }, bounds };
}
